import { type SearchItemGroupV3, type SpotlightActionV3 } from './searchV3.types'

// Group SpotlightActionV3 entries by their `group` label, sort items
// alphabetically within each group, and sort groups alphabetically. Each
// item's onClick is wrapped to invoke `onClickAfter` first (used by the
// menu to clear search state and reset the active item index).
export const formCategoriesIntoItemGroupsV3 = (
  actions: SpotlightActionV3[],
  onClickAfter: () => void
): SearchItemGroupV3[] => {
  const headers = actions
    .map(action => action.group)
    .filter((value, index, self) => self.indexOf(value) === index)

  const groups: SearchItemGroupV3[] = headers
    .map(group => ({
      groupHeaderText: group,
      items: actions
        .filter(action => action.group === group)
        .map(action => ({
          label: action.label,
          route: action.route ?? '',
          onClick: action.onClick,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.groupHeaderText.localeCompare(b.groupHeaderText))

  groups.forEach(group => {
    group.items.forEach(item => {
      const originalOnClick = item.onClick
      item.onClick = () => {
        onClickAfter()
        originalOnClick()
      }
    })
  })

  return groups
}

// Case-insensitive substring match. Whole group passes through if its header
// matches; otherwise items are filtered by label match. Empty `search` returns
// the input unchanged.
export const filterGroupsBySearch = (
  groups: SearchItemGroupV3[],
  search: string
): SearchItemGroupV3[] => {
  if (!search) return groups
  const needle = search.toLowerCase()

  const filtered: SearchItemGroupV3[] = []
  groups.forEach(group => {
    if (group.groupHeaderText.toLowerCase().includes(needle)) {
      filtered.push(group)
    } else {
      const items = group.items.filter(item => item.label.toLowerCase().includes(needle))
      if (items.length > 0) {
        filtered.push({ ...group, items })
      }
    }
  })
  return filtered
}

export const getTrackEventOpenSearchBarDataV3 = () => ({
  id: 'navV3.searchSpotlight.open',
  description: 'User is opening the search bar',
})

export const getTrackEventCloseSearchBarDataV3 = (searchValue: string) => ({
  id: 'navV3.searchSpotlight.close',
  description: 'User is closing the search bar',
  data: { searchValue },
})

export const getTrackEventUserSearchedDataV3 = (searchValue: string) => ({
  id: 'navV3.searchSpotlight.userSearched',
  description: 'User is searching for something',
  data: { searchValue },
})

export const getTrackEventNothingFoundDataV3 = (searchValue: string) => ({
  id: 'navV3.searchSpotlight.nothingFound',
  description: 'User is searching for a non-existent item',
  data: { searchValue },
})

export const getNavigateToSearchResultDataV3 = (
  searchValue: string,
  label: string,
  route: string,
  navigateBy: string
) => ({
  id: 'navV3.searchSpotlight.navigate',
  description: 'User is navigating to a search result',
  data: { searchValue, label, route, navigateBy },
})
